import { type AppEnv, runningEnv } from '../env/appEnv';
import type { SupportedLanguage } from './languages';
import type { TranslationMap } from './translate';

export const I18N_CACHE_KEY_PRODUCTION = 'lorath_i18n_react_v1';
export const I18N_CACHE_KEY_BETA = 'lorath_i18n_react_beta_v1';

/**
 * R6-02-c: eigener Cache-Schluessel je Umgebung. Beide Umgebungen liefern
 * denselben Sprachschluessel-Raum, aber unterschiedliche Staende — ein
 * geteilter Cache zeigte nach dem Wechsel die Texte der anderen Umgebung.
 * Der Produktions-Schluessel bleibt BITGLEICH.
 */
export function i18nCacheKeyFor(env: AppEnv): string {
  return env === 'beta' ? I18N_CACHE_KEY_BETA : I18N_CACHE_KEY_PRODUCTION;
}

export const I18N_CACHE_KEY = i18nCacheKeyFor(runningEnv());
export const I18N_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CacheEntry {
  lang: SupportedLanguage;
  timestamp: number;
  keys: TranslationMap;
}

interface CachePayload {
  v: 1;
  active?: CacheEntry;
  en?: CacheEntry;
}

export interface CachedI18nMaps {
  active: TranslationMap;
  en: TranslationMap;
}

function isFresh(entry: CacheEntry | undefined, now: number): entry is CacheEntry {
  return !!entry && now - entry.timestamp <= I18N_CACHE_TTL_MS;
}

function parseCache(): CachePayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(I18N_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachePayload>;
    return parsed.v === 1 ? parsed as CachePayload : null;
  } catch {
    return null;
  }
}

export function readCachedI18n(lang: SupportedLanguage, now = Date.now()): CachedI18nMaps | null {
  const cache = parseCache();
  if (!cache) return null;

  const active = cache.active?.lang === lang && isFresh(cache.active, now) ? cache.active.keys : {};
  const en = cache.en?.lang === 'en' && isFresh(cache.en, now) ? cache.en.keys : {};
  return Object.keys(active).length || Object.keys(en).length ? { active, en } : null;
}

export function writeCachedI18n(lang: SupportedLanguage, active: TranslationMap, en: TranslationMap, now = Date.now()): void {
  if (typeof window === 'undefined') return;
  const payload: CachePayload = {
    v: 1,
    active: { lang, timestamp: now, keys: active },
    en: { lang: 'en', timestamp: now, keys: en },
  };

  try {
    window.localStorage.setItem(I18N_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage darf in Private-Modi scheitern; Netzwerk-Fallback bleibt aktiv.
  }
}
